<template>
  <UiDatePicker
    v-model="model"
    data-testid="datePickerDesktop"
    :utc="false"
    :auto-apply="true"
    :teleport="false"
    :locale="localeObj"
    :max-date="today"
    :min-date="hasSearchUnlimited() ? undefined : lastYear"
    :multi-calendars="isGreaterThenMedium"
    :auto-position="true"
    :show-last-in-range="true"
    trigger-width="full"
    :custom-format-callback="customFormatCallback"
    :quick-filter-ranges="QUICK_FILTER_DATE_RANGES"
    :trigger-placeholder-text="placeHolderText"
    :left-sidebar="isGreaterThenMedium"
    :quick-filter-headline="t('placeholder.quickFilterHeadline')"
    @quick-filter-changed="handleQuickFilterChange"
    @update:model-value="handleDateChange"
    @closed="closeDatepicker"
    @open="openDatepicker">
    <template #action-extra>
      <div class="text-left">
        <UiButton
          appearance="outline-emphasis"
          size="xs"
          :disabled="!model"
          @click="resetDatepicker()">
          <UiIcon name="reset" />
          <span>{{ t("control.reset") }}</span>
        </UiButton>
      </div>
    </template>
  </UiDatePicker>
</template>

<script lang="ts" setup>
import UiButton from "@dpa-id-components/dpa-shared-components/src/components/UiButton/UiButton.vue";
import UiDatePicker from "@dpa-id-components/dpa-shared-components/src/components/UiDatePicker/UiDatePicker.vue";
import UiIcon from "@dpa-id-components/dpa-shared-components/src/components/UiIcon/UiIcon.vue";
import { sub } from "date-fns";
import { de, enGB, es } from "date-fns/locale";
import { computed } from "vue";
import { useI18n } from "vue-i18n";

import useAuthorization from "@/composition/shared/useAuthorization.ts";
import useBreakpoints from "@/composition/shared/useBreakpoints.ts";
import { useTracking } from "@/composition/shared/useTracking.ts";
import type { DateType, PresetRange } from "@/stores/search.ts";
import { RelativeDateRange } from "@/types/enums.ts";
import { isDateRange, relativeDateLookup } from "@/utils/dateRangeUtils.ts";

const { t } = useI18n();
const QUICK_FILTER_DATE_RANGES: PresetRange[] = [
  {
    label: t("ui.datepicker.today"),
    name: RelativeDateRange.TODAY,
    range: relativeDateLookup[RelativeDateRange.TODAY],
  },
  {
    label: t("ui.datepicker.this_month"),
    name: RelativeDateRange.THIS_MONTH,
    range: relativeDateLookup[RelativeDateRange.THIS_MONTH],
  },
  {
    label: t("ui.datepicker.this_year"),
    name: RelativeDateRange.THIS_YEAR,
    range: relativeDateLookup[RelativeDateRange.THIS_YEAR],
  },
  {
    label: t("ui.datepicker.last_month"),
    name: RelativeDateRange.LAST_MONTH,
    range: relativeDateLookup[RelativeDateRange.LAST_MONTH],
  },
  {
    label: t("ui.datepicker.last_year"),
    name: RelativeDateRange.LAST_YEAR,
    range: relativeDateLookup[RelativeDateRange.LAST_YEAR],
  },
  {
    label: t("ui.datepicker.last_30_days"),
    name: RelativeDateRange.LAST_30_DAYS,
    range: relativeDateLookup[RelativeDateRange.LAST_30_DAYS],
  },
  {
    label: t("ui.datepicker.last_365_days"),
    name: RelativeDateRange.LAST_365_DAYS,
    range: relativeDateLookup[RelativeDateRange.LAST_365_DAYS],
  },
];

const { isGreaterThenMedium } = useBreakpoints();
const { trackEvent } = useTracking();
const { hasSearchUnlimited } = useAuthorization();
const { locale } = useI18n();

const model = defineModel<[Date, Date]>();
const { customFormatCallback = undefined } = defineProps<{
  customFormatCallback?: (date: Date | [Date, Date]) => string;
}>();

const localeObj = computed(() => {
  if (locale.value == "de") {
    return de;
  } else if (locale.value == "en") {
    return enGB;
  } else if (locale.value == "es") {
    return es;
  } else {
    return de;
  }
});

const emit = defineEmits<{
  opened: [];
  closed: [];
  reset: [];
  dateChanged: [[Date, Date]];
  quickFilterChanged: [PresetRange];
}>();

const handleDateChange = (date: DateType | undefined) => {
  if (!date || !isDateRange(date)) return;
  emit("dateChanged", date);
  trackEvent("leftNavigationBar_click_button_changeDate");
};

const handleQuickFilterChange = (event: PresetRange) => {
  if (!event) return;
  emit("quickFilterChanged", event);
};

const closeDatepicker = () => {
  emit("closed");
  trackEvent("leftNavigationBar_click_filterButton_closeDatepicker");
};

const openDatepicker = () => {
  emit("opened");
  trackEvent("leftNavigationBar_click_filterButton_openDatepicker");
};

const resetDatepicker = () => {
  emit("reset");
  trackEvent("leftNavigationBar_click_button_resetDatepicker");
};
const placeHolderText = computed(() => {
  return t(`placeholder.defaultDateRange`);
});

const today = new Date();
const lastYear = sub(new Date(), { years: 1 });
</script>
